import { useEffect, useMemo, useState } from "react";
import { Copy, Image as ImageIcon, Send, Sparkles, TrendingDown, TrendingUp } from "lucide-react";
import { useWallet } from "@/context/WalletContext";
import { useTranslation } from "@/lib/i18n/useTranslation";

interface CalloutPost {
  id: string;
  author: string;
  handle: string;
  message: string;
  coin: string;
  position: "long" | "short" | "neutral";
  size: string;
  entry: string;
  notes: string;
  imageUrl?: string;
  wallet?: string;
  source?: string;
  autoReason?: string;
  positionValue?: number;
  copiers: number;
  createdAt: string;
}

export function AgentCallouts() {
  const { xProfile } = useWallet();
  const { t } = useTranslation();
  const [posts, setPosts] = useState<CalloutPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [coin, setCoin] = useState("SOL");
  const [position, setPosition] = useState<CalloutPost["position"]>("long");
  const [size, setSize] = useState("1.2");
  const [entry, setEntry] = useState("$0.41");
  const [notes, setNotes] = useState("Clean structure, strong liquidity");
  const [imageUrl, setImageUrl] = useState("");
  const [walletAddress, setWalletAddress] = useState("");
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const loadPosts = async () => {
      try {
        const response = await fetch("/api/callouts");
        if (!response.ok) throw new Error("Failed to load");
        const data = await response.json();
        if (!active) return;

        const mapped = Array.isArray(data)
          ? data.map((item: any) => ({
              id: item.id,
              author: item.author || "Anonymous",
              handle: item.handle || "anonymous",
              message: item.message || "",
              coin: item.coin || "SOL",
              position: (item.position === "short" || item.position === "neutral" ? item.position : "long") as CalloutPost["position"],
              size: item.size || "1.0",
              entry: item.entry || "$0.00",
              notes: item.notes || "",
              imageUrl: item.imageUrl || undefined,
              wallet: item.wallet || undefined,
              source: item.source || undefined,
              autoReason: item.autoReason || undefined,
              positionValue: typeof item.positionValue === "number" ? item.positionValue : item.positionValue ? Number(item.positionValue) : undefined,
              copiers: Number(item.copiers || 0),
              createdAt: typeof item.createdAt === "number" ? new Date(item.createdAt).toLocaleString() : item.createdAt || "just now",
            }))
          : [];

        setPosts(mapped);
      } catch {
        if (active) setPosts([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    void loadPosts();
    return () => {
      active = false;
    };
  }, []);

  const handlePost = async () => {
    if (!xProfile) {
      setNotice(t("wallet.xLoginRequired"));
      return;
    }

    const trimmed = message.trim();
    if (!trimmed) {
      setNotice(t("ops.composeHint"));
      return;
    }

    try {
      const response = await fetch("/api/callouts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          author: xProfile.displayName || xProfile.username,
          handle: xProfile.username,
          message: trimmed,
          coin: coin.trim() || "SOL",
          position,
          size: size.trim() || "1.0",
          entry: entry.trim() || "$0.00",
          notes: notes.trim() || "New callout",
          imageUrl: imageUrl.trim() || undefined,
          wallet: walletAddress.trim() || undefined,
          copiers: 0,
        }),
      });

      if (!response.ok) throw new Error("Failed to persist");
      const created = await response.json();

      setPosts((prev) => [{
        id: created.id,
        author: created.author || xProfile.displayName || xProfile.username,
        handle: created.handle || xProfile.username,
        message: created.message || trimmed,
        coin: created.coin || coin.trim() || "SOL",
        position: (created.position === "short" || created.position === "neutral" ? created.position : "long") as CalloutPost["position"],
        size: created.size || size.trim() || "1.0",
        entry: created.entry || entry.trim() || "$0.00",
        notes: created.notes || notes.trim() || "New callout",
        imageUrl: created.imageUrl || undefined,
        wallet: created.wallet || walletAddress.trim() || undefined,
        source: created.source || undefined,
        autoReason: created.autoReason || undefined,
        positionValue: typeof created.positionValue === "number" ? created.positionValue : undefined,
        copiers: Number(created.copiers || 0),
        createdAt: typeof created.createdAt === "number" ? new Date(created.createdAt).toLocaleString() : created.createdAt || "just now",
      }, ...prev]);
      setMessage("");
      setCoin("SOL");
      setPosition("long");
      setSize("1.2");
      setEntry("$0.41");
      setNotes("Clean structure, strong liquidity");
      setImageUrl("");
      setNotice(null);
    } catch {
      setNotice("Unable to save the callout right now.");
    }
  };

  const copyTrade = async (id: string) => {
    try {
      const response = await fetch(`/api/callouts/${id}/copy`, { method: "POST" });
      if (!response.ok) throw new Error("Failed to copy");
      const updated = await response.json();
      setPosts((prev) => prev.map((post) => (post.id === id ? { ...post, copiers: Number(updated?.copiers || post.copiers + 1) } : post)));
      setNotice(t("ops.copying"));
    } catch {
      setNotice("Unable to record that copy trade.");
    }
  };

  const visiblePosts = useMemo(() => posts, [posts]);

  return (
    <div className="flex h-full flex-col overflow-hidden bg-[var(--color-bg-sideBar)]">
      <div className="flex-1 overflow-y-auto px-3 py-3">
        <div className="rounded-[1.5rem] border border-cyan-400/20 bg-gradient-to-br from-cyan-500/10 via-slate-900/90 to-purple-500/10 p-4 shadow-[0_0_30px_rgba(34,211,238,0.08)]">
          <div className="flex items-center gap-2 text-cyan-300">
            <Sparkles className="size-4" />
            <span className="text-[10px] font-black uppercase tracking-[0.35em]">{t("ops.composeTitle")}</span>
          </div>
          <p className="mt-2 text-sm text-white/70">{t("ops.composeHint")}</p>

          <div className="mt-4 space-y-3">
            <input
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t("ops.messagePlaceholder")}
              className="w-full rounded-2xl border border-white/10 bg-black/20 px-3 py-3 text-sm text-white outline-none placeholder:text-white/35"
            />

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <input
                value={coin}
                onChange={(e) => setCoin(e.target.value)}
                placeholder={t("ops.coinPlaceholder")}
                className="rounded-2xl border border-white/10 bg-black/20 px-3 py-3 text-sm text-white outline-none placeholder:text-white/35"
              />
              <div className="rounded-2xl border border-white/10 bg-black/20 p-3">
                <div className="mb-2 text-[10px] font-black uppercase tracking-[0.28em] text-white/40">{t("ops.positionType")}</div>
                <div className="flex flex-wrap gap-2">
                  {(["long", "short", "neutral"] as CalloutPost["position"][]).map((option) => {
                    const active = position === option;
                    return (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setPosition(option)}
                        className={`rounded-full px-3 py-1.5 text-sm font-semibold transition ${active ? "bg-cyan-400 text-slate-950" : "bg-white/10 text-white/70 hover:bg-white/15"}`}
                      >
                        {t(`ops.position${option === "long" ? "Long" : option === "short" ? "Short" : "Neutral"}`)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <input
                value={size}
                onChange={(e) => setSize(e.target.value)}
                placeholder={t("ops.size")}
                className="rounded-2xl border border-white/10 bg-black/20 px-3 py-3 text-sm text-white outline-none placeholder:text-white/35"
              />
              <input
                value={entry}
                onChange={(e) => setEntry(e.target.value)}
                placeholder={t("ops.entry")}
                className="rounded-2xl border border-white/10 bg-black/20 px-3 py-3 text-sm text-white outline-none placeholder:text-white/35"
              />
            </div>

            <input
              value={walletAddress}
              onChange={(e) => setWalletAddress(e.target.value)}
              placeholder={t("ops.walletAddress")}
              className="w-full rounded-2xl border border-white/10 bg-black/20 px-3 py-3 text-sm text-white outline-none placeholder:text-white/35"
            />

            <input
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder={t("ops.imagePlaceholder")}
              className="w-full rounded-2xl border border-white/10 bg-black/20 px-3 py-3 text-sm text-white outline-none placeholder:text-white/35"
            />

            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("ops.notes")}
              className="w-full rounded-2xl border border-white/10 bg-black/20 px-3 py-3 text-sm text-white outline-none placeholder:text-white/35"
            />

            <div className="flex items-center justify-between gap-3">
              <div className="text-xs text-white/45">{xProfile ? `@${xProfile.username}` : t("wallet.xLoginRequired")}</div>
              <button
                onClick={handlePost}
                className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300"
              >
                <Send className="size-4" />
                {t("ops.post")}
              </button>
            </div>
            {notice ? <div className="rounded-xl border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">{notice}</div> : null}
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {loading && posts.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/55">Loading callouts…</div>
          ) : visiblePosts.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/55">{t("ops.empty")}</div>
          ) : (
            visiblePosts.map((post) => {
              const isLong = post.position === "long";
              const isShort = post.position === "short";
              return (
                <article key={post.id} className="overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#111318] shadow-[0_10px_30px_rgba(0,0,0,0.25)]">
                  <div className="flex items-start gap-3 p-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-cyan-400 to-purple-500 font-semibold text-white">
                      {post.author.charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white">{post.author}</span>
                        <span className="text-xs text-white/45">@{post.handle}</span>
                        <span className="text-xs text-white/35">• {post.createdAt}</span>
                      </div>
                      <p className="mt-2 text-sm leading-6 text-white/80">{post.message}</p>
                    </div>
                  </div>

                  {post.imageUrl ? (
                    <div className="mx-4 mb-3 overflow-hidden rounded-[1.25rem] border border-white/10">
                      <img src={post.imageUrl} alt={post.coin} className="h-40 w-full object-cover" />
                    </div>
                  ) : (
                    <div className="mx-4 mb-3 flex h-24 items-center justify-center rounded-[1.25rem] border border-dashed border-cyan-400/25 bg-gradient-to-br from-cyan-500/10 to-purple-500/10">
                      <div className="flex items-center gap-2 text-cyan-300">
                        <ImageIcon className="size-4" />
                        <span className="text-sm">{post.coin} callout</span>
                      </div>
                    </div>
                  )}

                  <div className="mx-4 mb-4 rounded-[1.25rem] border border-white/10 bg-white/[0.03] p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        {isLong ? <TrendingUp className="size-4 text-emerald-400" /> : isShort ? <TrendingDown className="size-4 text-rose-400" /> : <Sparkles className="size-4 text-cyan-400" />}
                        <span className="text-sm font-semibold text-white">{post.coin}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] ${isLong ? "bg-emerald-500/15 text-emerald-300" : isShort ? "bg-rose-500/15 text-rose-300" : "bg-cyan-500/15 text-cyan-300"}`}>
                          {t(`ops.position${post.position === "long" ? "Long" : post.position === "short" ? "Short" : "Neutral"}`)}
                        </span>
                        {post.source === "auto" ? (
                          <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-200">
                            {t("ops.sourceAuto")}
                          </span>
                        ) : null}
                      </div>
                      <div className="text-right text-[11px] text-white/45">
                        <div>{post.notes}</div>
                      </div>
                    </div>
                    <div className="mt-3 rounded-xl border border-cyan-400/20 bg-cyan-500/10 px-3 py-2 text-[11px] uppercase tracking-[0.25em] text-cyan-300">
                      {post.positionValue != null
                        ? t("ops.positionValue", { value: post.positionValue.toFixed(0) })
                        : t("ops.positionSnapshot")}
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                      <div className="rounded-xl bg-black/20 p-2">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-white/35">{t("ops.size")}</div>
                        <div className="font-semibold text-white">{post.size}</div>
                      </div>
                      <div className="rounded-xl bg-black/20 p-2">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-white/35">{t("ops.entry")}</div>
                        <div className="font-semibold text-white">{post.entry}</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 border-t border-white/10 px-4 py-3 text-sm text-white/55">
                    {post.wallet ? (
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-xs text-white/40">{t("ops.tradeWallet")}: <span className="font-mono text-white/80">{post.wallet}</span></span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(post.wallet || "");
                            setNotice(t("ops.copyWalletNotice"));
                          }}
                          className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1.5 text-white/70 transition hover:bg-white/10"
                        >
                          <Copy className="size-3" />
                          {t("ops.copyWallet")}
                        </button>
                      </div>
                    ) : null}
                    {post.autoReason ? (
                      <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-[11px] text-white/60">
                        {t("ops.autoReasonLabel")}: {post.autoReason}
                      </div>
                    ) : null}
                    <div className="flex items-center justify-between">
                      <div>{post.copiers} {t("ops.copyTrade")}{post.copiers === 1 ? "" : "s"}</div>
                      <button onClick={() => copyTrade(post.id)} className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1.5 text-white/70 transition hover:bg-white/10">
                        <Copy className="size-3" />
                        {t("ops.copyTrade")}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
